import { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCryptoTransactions } from "../../../redux/actions/Transaction/TransactionHistory";
import { setPageTitle } from "../../../helper/metaDataHelper";
import TransactionDetailsPanel from "./TransactionDetailsPanel";
import TransactionListPanel from "./TransactionListPanel";
import { scrollToTopWithEase } from "../../../helper/ScrollHelper";

const TYPE_MAP = {
  "Buy": "buy",
  "Sell": "sell",
  "Deposit": "deposit",
  "Withdraw": "withdraw",
};

const PERIOD_MAP = {
  "Today": "today",
  "Last 7 Days": "7days",
  "This Month": "this_month",
  "Last 3 Months": "3months",
  "Last Year": "1year",
};

const TransactionHistory = ({ onPageBack }) => {
  const dispatch = useDispatch();

  // Redux Selectors
  const userInfo = useSelector((state) => state.userInfo?.userInfo);
  const cryptoAccounts = useSelector((state) => state.client?.clientInfo?.crypto_accounts || []);

  // UI State
  const [selected, setSelected] = useState(null);

  // Client-Side Only Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAssets, setSelectedAssets] = useState([]);

  // API Query Parameter States
  const [typeFilter, setTypeFilter] = useState("All Transactions");
  const [dateRange, setDateRange] = useState("All Time");
  const [transactionStatus, setTransactionStatus] = useState("");

  // Data & Loading States
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadMore, setLoadMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [downloadPDFLoading] = useState(false);

  const limit = 20;

  // Set Page Title dynamically
  useEffect(() => {
    setPageTitle(`Crypto Transactions`);
    scrollToTopWithEase();
  }, []);

  const buildPayload = (cursor = null) => {
    const payload = {
      client_id: userInfo?.client_id,
      status: transactionStatus || undefined,
      type: TYPE_MAP[typeFilter] || null,
      period: PERIOD_MAP[dateRange] || null,
      limit,
    };

    if (selectedAssets?.length > 0) {
      payload.account_ids = selectedAssets;
    }

    if (cursor) {
      payload.cursor = cursor;
    }

    return payload;
  };

  // Fetch Transactions from API
  const fetchTransactions = useCallback(async () => {
    if (!userInfo?.client_id) return;

    try {
      setLoading(true);

      const response = await dispatch(getCryptoTransactions(buildPayload()));

      setTransactions(response?.data ?? []);
      setNextCursor(response?.next_cursor ?? null);
      setHasMore(Boolean(response?.next_cursor));
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [
    dispatch,
    userInfo?.client_id,
    selectedAssets,
    transactionStatus,
    typeFilter,
    dateRange,
    limit,
    // buildPayload
  ]);

  // Load More Transactions (Pagination)
  const loadMoreTransactions = useCallback(async () => {
    if (loading || loadMore || !hasMore || !nextCursor) return;

    try {
      setLoadMore(true);

      const response = await dispatch(getCryptoTransactions(buildPayload(nextCursor)));

      setTransactions((prev) => [...prev, ...(response?.data ?? [])]);
      setNextCursor(response?.next_cursor ?? null);
      setHasMore(Boolean(response?.next_cursor));
    } catch (error) {
      console.error("Failed to load more transactions:", error);
    } finally {
      setLoadMore(false);
    }
    // eslint-disable-next-line
  }, [
    dispatch,
    loading,
    loadMore,
    hasMore,
    nextCursor,
    userInfo?.client_id,
    selectedAssets,
    transactionStatus,
    typeFilter,
    dateRange,
    limit,
    // buildPayload
  ]);

  // Client-Side Search Filter Logic
  const filteredTransactions = useMemo(() => {
    if (!searchQuery.trim()) return transactions;

    const query = searchQuery.toLowerCase().trim();

    return transactions.filter((tx) => {
      const idMatch = tx?.id?.toString().toLowerCase().includes(query);
      const txHashMatch = tx?.tx_hash?.toLowerCase().includes(query);
      const typeMatch = tx?.type?.toLowerCase().includes(query);
      const assetMatch =
        tx?.currency_code?.toLowerCase().includes(query) ||
        tx?.transaction_date?.toLowerCase().includes(query) ||
        tx?.remarks?.toLowerCase().includes(query);
      const amountMatch = tx?.amount?.toString().includes(query);

      return idMatch || txHashMatch || typeMatch || assetMatch || amountMatch;
    });
  }, [transactions, searchQuery]);

  // Reset Filters
  const resetFilters = useCallback(() => {
    setSearchQuery("");
    setTypeFilter("All Transactions");
    setSelectedAssets([]);
    setDateRange("All Time");
    setTransactionStatus("");
    setSelected(null);
  }, []);

  // Fetch data on initial mount or when API filters change
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Reset filters on component unmount/reset
  useEffect(() => {
    resetFilters();
  }, [resetFilters]);

  return (
    <div>
      <div className="flex flex-1 flex-col bg-background-surface p-2 md:p-4 max-sm:rounded-none sm:rounded-[16px] sm:p-6 sm:shadow-[0px_4px_12px_rgba(0,0,0,0.08)]">
        {/* Desktop View */}
        <div className="hidden lg:flex lg:flex-row lg:items-start lg:gap-9">
          <TransactionListPanel
            transactions={filteredTransactions}
            selected={selected}
            searchQuery={searchQuery}
            typeFilter={typeFilter}
            selectedAssets={selectedAssets}
            cryptoAccounts={cryptoAccounts}
            dateRange={dateRange}
            onSearchChange={setSearchQuery}
            onTypeFilterChange={setTypeFilter}
            onAssetFilterChange={setSelectedAssets}
            onDateRangeChange={setDateRange}
            onSelect={(transaction) => {
              setSelected(transaction);
              scrollToTopWithEase();
            }}
            onPageBack={onPageBack}
            onLoadMore={loadMoreTransactions}
            hasMore={hasMore}
            loadMore={loadMore}
            loadingTransactions={loading}
          />

          <div
            className="w-px shrink-0 self-stretch bg-[#e8e8e8]"
            aria-hidden
          />

          <TransactionDetailsPanel
            transaction={selected}
            isDownloading={downloadPDFLoading}
            onCancel={() => setSelected(null)}
          />
        </div>

        {/* Mobile View */}
        <div className="lg:hidden">
          {selected ? (
            <TransactionDetailsPanel
              transaction={selected}
              onBack={() => setSelected(null)}
              onCancel={() => setSelected(null)}
              isDownloading={downloadPDFLoading}
            />
          ) : (
            <TransactionListPanel
              transactions={filteredTransactions}
              selected={selected}
              searchQuery={searchQuery}
              typeFilter={typeFilter}
              selectedAssets={selectedAssets}
              cryptoAccounts={cryptoAccounts}
              dateRange={dateRange}
              onSearchChange={setSearchQuery}
              onTypeFilterChange={setTypeFilter}
              onAssetFilterChange={setSelectedAssets}
              onDateRangeChange={setDateRange}
              onSelect={setSelected}
              onPageBack={onPageBack}
              onLoadMore={loadMoreTransactions}
              hasMore={hasMore}
              loadMore={loadMore}
              loadingTransactions={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;